<template>
  <div class="temporary-candidate-root">
    <QHeader class="bg-white text-primary">
      <QToolbar>
        <QBtn flat round dense icon="icon-f-arrow-left" @click="onGoBack" />
        <QToolbarTitle>
          {{ $t("ETAB.PAGES.EXTRA.@TITLE") }}
        </QToolbarTitle>
        <QBtn
          v-if="canDeclareProblem"
          class="q-pa-xs"
          outline
          color="secondary"
          no-caps
          dense
          icon-right="warning_amber"
          :label="$t('ETAB.PAGES.RECRUITMENT.@REPORT_PROBLEM')"
          @click="onReportProblem"
        />
      </QToolbar>
      <QTabs v-if="hasMissionTabs" dense align="justify">
        <QRouteTab no-caps :to="missionRoute" :label="$t('ETAB.PAGES.EXTRA.@MISSION_TAB')" />
        <QRouteTab no-caps :to="profileRoute" :label="$t('ETAB.PAGES.EXTRA.@PROFILE_TAB')" />
      </QTabs>
    </QHeader>

    <QPageContainer>
      <QPage>
        <router-view />
      </QPage>
    </QPageContainer>

    <QFooter class="bg-transparent">
      <FooterBtnsWrapper>
        <ExtraBtnIcon
          v-if="canSelect && !recruitment.isBookingExpress()"
          class="full-width"
          color="secondary"
          type="button"
          no-caps
          :disable="isGlobalLoadingActive"
          :all-space="true"
          icon-name="icon-extra-magic"
          :btn-label="$t('ETAB.PAGES.EXTRA.@SELECT_EXTRA')"
          @click="onSelectExtra"
        />

        <QBanner v-if="extraHasCanceled" class="full-width bg-grey text-center text-bold">
          <span class="text-h3">{{ $t("ETAB.PAGES.EXTRA.@CANCELED_EXTRA") }}</span>
        </QBanner>
      </FooterBtnsWrapper>
      <Contact v-if="canAccessContactInformations" :phone="extra.getIdentity().phone" :email="extra.getIdentity().email" />
    </QFooter>

    <IconTextConfirmModal
      v-model="problemModalOpened"
      :backdrop-dismiss="true"
      :on-confirm-modal="declareExtraMissingMER"
      :on-close-modal="contactEtabSupport"
      icon="icon-f-alert-octagon"
      :confirm="$t('ETAB.PAGES.EXTRA.REPORT_PROBLEM.@ACTION_DECLARE_MISSING')"
      :refuse="$t('ETAB.PAGES.EXTRA.REPORT_PROBLEM.@ACTION_CONTACT_SUPPORT')"
      :text="$t('ETAB.PAGES.EXTRA.REPORT_PROBLEM.@TITLE', { name: extra.getIdentity().firstname })"
      confirm-color="secondary"
      refuse-color="primary-medium"
    />

    <ExtraSelectedModal v-model:opened="successModalOpened" />
    <ExtraGoCardlessModal
      v-model:opened="goCardlessModalOpened"
      :has-epct-feature-flag="profile.hasEpctFeatureFlag()"
      @on-click-iban-btn="openIbanLink"
      @on-click-already-done="onClickAlreadyDone"
    />
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onBeforeMount, inject } from "vue";
  import { useRouter, useRoute } from "vue-router";
  import { storeToRefs } from "pinia";
  import { useI18n } from "vue-i18n";
  import { JavascriptHelpers } from "@core/helpers";
  import { ContactManager } from "@core/managers/contact.manager";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { ScrollManager } from "@core/managers/scroll.manager";
  import { EtabProfileService, ExtraService, RecruitmentCandidatesService } from "@core/services";
  import { IconTextConfirmModal } from "@core/components/modals";
  import { GO_CARDLESS } from "@core/constants";
  import { ETAB_ROUTES } from "@etab/config";
  import { LoadingManager } from "@core/managers/loading.manager";
  import { repositoryManager } from "@extracadabra/infrastructure";
  import {
    GETTER_RECRUITMENT_GET_CURRENT,
    GETTER_EXTRA_GET_CURRENT,
    ACTION_EXTRA_GET_BY_ID,
    ACTION_RECRUITMENT_GET,
    GETTER_PROFILE_GET,
    ACTION_EXTRA_GET_BIO_BY_ID,
  } from "@etab/stores/types";
  import { useExtraStore } from "@etab/stores/extra";
  import { useProfileStore } from "@etab/stores/profile";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import Contact from "@etab/components/common/Contact.component.vue";
  import ExtraSelectedModal from "@etab/components/extra/ExtraSelected.modal.vue";
  import ExtraGoCardlessModal from "@etab/components/extra/GoCardless.modal.vue";

  // Composition API starts here
  const router = useRouter();
  const route = useRoute();
  const { t } = useI18n();

  // Store setup
  const profileStore = useProfileStore();
  const extraStore = useExtraStore();
  const recruitmentStore = useRecruitmentStore();

  // Get store references
  const { [GETTER_PROFILE_GET]: profile } = storeToRefs(profileStore);
  const { [GETTER_EXTRA_GET_CURRENT]: extra } = storeToRefs(extraStore);
  const { [GETTER_RECRUITMENT_GET_CURRENT]: recruitment } = storeToRefs(recruitmentStore);
  const repository = repositoryManager.get("candidateProfile");
  // Local reactive state
  const successModalOpened = ref(false);
  const problemModalOpened = ref(false);
  const goCardlessModalOpened = ref(false);
  const isGlobalLoadingActive = ref(false);
  const missionRoute = ref({});
  const profileRoute = ref({});
  const recruitmentDetailRoute = ref({});
  const appliedCandidatesRoute = ref({});
  const selectedCandidatesRoute = ref({});

  // Route params
  const demandId = computed(() => route.params.demandId);
  const linkedId = computed(() => route.params.linkedId);
  const demandType = computed(() => route.params.demandType);
  const candidateId = computed(() => route.params.candidateId);
  const demandTemporaryId = computed(() => route.params.demandTemporaryId);

  // Computed properties
  const isExtraBooked = computed(() => extra.value.isSelected());
  const extraHasCanceled = computed(() => extra.value.isCanceled());
  const hasMissionTabs = computed(() => recruitment.value.isAE() && (isExtraBooked.value || extraHasCanceled.value));
  const canAccessContactInformations = computed(() =>
    ExtraService.canAccessContactInformations({
      extra: extra.value,
      recruitment: recruitment.value,
    }),
  );
  const canDeclareProblem = computed(
    () => recruitment.value.hasStarted() && !recruitment.value.hasEnded() && isExtraBooked.value && !extraHasCanceled.value,
  );
  const canSelect = computed(() => !recruitment.value.hasEnded() && !isExtraBooked.value && !extraHasCanceled.value);

  // Methods
  const initRoutes = () => {
    const currentDemandId = demandId.value;
    const currentDemandType = demandType.value;
    const currentCandidateId = candidateId.value;

    missionRoute.value = {
      name: ETAB_ROUTES.dashboard.recruitment.temporary.candidate.mission.name,
      params: { demandId: currentDemandId, demandType: currentDemandType, candidateId: currentCandidateId },
    };
    profileRoute.value = {
      name: ETAB_ROUTES.dashboard.recruitment.temporary.candidate.profile.name,
      params: { demandId: currentDemandId, demandType: currentDemandType, candidateId: currentCandidateId },
    };
    recruitmentDetailRoute.value = {
      name: ETAB_ROUTES.dashboard.recruitment.temporary.detail.root.name,
      params: { demandId: currentDemandId, demandType: currentDemandType },
    };
    appliedCandidatesRoute.value = {
      name: ETAB_ROUTES.dashboard.recruitment.temporary.detail.applied.name,
      params: { demandId: currentDemandId, demandType: currentDemandType },
    };
    selectedCandidatesRoute.value = {
      name: ETAB_ROUTES.dashboard.recruitment.temporary.detail.selected.name,
      params: { demandId: currentDemandId, demandType: currentDemandType },
    };
  };

  const startGlobalLoading = () => {
    isGlobalLoadingActive.value = true;
  };

  const stopGlobalLoading = () => {
    isGlobalLoadingActive.value = false;
  };

  const openSuccessModal = () => {
    successModalOpened.value = true;
  };

  const openGoCardlessModal = () => {
    goCardlessModalOpened.value = true;
  };

  const openProblemModal = () => {
    problemModalOpened.value = true;
  };

  const openIbanLink = () => {
    if (profile.value.hasEpctFeatureFlag()) {
      router
        .push({
          name: ETAB_ROUTES.payment.iban.name,
          query: { url: route.path },
        })
        .catch(() => {});
    } else {
      JavascriptHelpers.openUrl(GO_CARDLESS, "_blank");
    }
  };

  const onClickAlreadyDone = async () => {
    const message = "WARNINGS.@GO_CARDLESS_PLEASE_WAIT";
    const color = "warning";
    const etabId = await EtabProfileService.getEtabId();
    // Référencer la fonction $t correctement
    const subject = t("EMAILS.@GO_CARDLESS_EMAIL_SUBJECT", { id: etabId });

    NotificationsManager.contactEtabSupport({ message, color, subject });
  };

  const refresh = async ({ displayLoading = true } = {}) => {
    try {
      if (displayLoading) {
        LoadingManager.show();
      }
      const currentDemandId = demandId.value;
      const currentDemandType = demandType.value;
      const type = recruitment.value.getType();
      const extraId = extra.value.getId();
      const currentLinkedId = linkedId.value;

      await recruitmentStore[ACTION_RECRUITMENT_GET]({
        demandId: currentDemandId,
        demandType: currentDemandType,
      });

      await extraStore[ACTION_EXTRA_GET_BY_ID]({
        extraId,
        demandId: currentLinkedId || currentDemandId,
        type,
      });
    } catch (error) {
      const message = (error && error.message) || "ERRORS.@SOMETHING_WENT_WRONG";
      NotificationsManager.error({ message });
      throw error;
    } finally {
      LoadingManager.hide();
    }
  };

  const onGoBack = () => {
    if (isExtraBooked.value || extraHasCanceled.value) {
      return router.push(selectedCandidatesRoute.value).catch(() => {});
    }
    return router.push(appliedCandidatesRoute.value).catch(() => {});
  };

  const onSelectExtra = () => {
    if (recruitment.value.isAE()) {
      selectExtraAE();
    }
  };

  const selectExtraAE = async () => {
    const currentDemandId = demandId.value;
    const extraId = extra.value.getId();
    const extraName = extra.value.getIdentity().firstname;
    const currentRecruitment = recruitment.value;
    try {
      LoadingManager.show();
      // we try to book regardless of the status of allowedAE
      await RecruitmentCandidatesService.bookExtraAE({
        recruitment: currentRecruitment,
        extraId,
      });
      await refresh({ displayLoading: false });
      await extraStore[ACTION_EXTRA_GET_BIO_BY_ID]({ demandId: demandTemporaryId.value, candidateId: extraId, repository });
      openSuccessModal();
    } catch (error) {
      const response = error?.request?.response;
      const status = error?.request?.status;

      switch (status) {
        case 400: // validation error
          NotificationsManager.error({ message: response });
          break;
        case 412: // extra not available
          NotificationsManager.error({ message: "ERRORS.@EXTRA_NOT_AVAILABLE" });
          break;
        case 456:
          // status code 456 means the etab is not allowed to book AE
          // the etab needs to register his IBAN with Go cardless or Stripe
          // here, depending on FEATURE FLAG EPCT, either go cardless or redirect to IBAN form
          openGoCardlessModal();
          break;
        default:
          // whatever happens outside of documented cases, we offer the possibility to contact support
          NotificationsManager.contactEtabSupport({
            subject: t("EMAILS.@ETAB_CONTACT_EXTRA_PROBLEM", {
              demandId: currentDemandId,
              extraName,
              extraId,
            }),
          });
          break;
      }
      throw error;
    } finally {
      LoadingManager.hide();
    }
  };

  const declareExtraMissingMER = async () => {
    try {
      LoadingManager.show();
      await RecruitmentCandidatesService.declareExtraMissing({
        recruitment: recruitment.value,
        id: linkedId.value,
      });

      await refresh();
      onGoBack();
    } catch (error) {
      LoadingManager.hide();
      NotificationsManager.contactEtabSupport();
      throw error;
    }
    // we stopGlobalLoading in the refresh method
  };

  const onReportProblem = () => {
    contactEtabSupport();
  };

  const contactEtabSupport = () => {
    const currentDemandId = demandId.value;
    const currentCandidateId = candidateId.value;
    const extraName = extra.value.getIdentity().firstname;
    // Référencer la fonction $t correctement
    const subject = t("EMAILS.@DEMAND_PROBLEM_EXTRA", {
      demandId: currentDemandId,
      extraName,
      extraId: currentCandidateId,
    });

    ContactManager.contactEtabSupport({ subject });
  };

  // Lifecycle hooks
  onBeforeMount(() => {
    ScrollManager.scrollToTop();
    initRoutes();
  });

  onMounted(() => {
    // stop loading started in routing
    LoadingManager.hide();
  });
</script>

<style lang="scss">
  .temporary-candidate-root {
    background-color: $body-background-light-grey;

    .q-page {
      overflow: hidden; // remove the scroll during transition
      padding: 0 15px 15px;
    }
  }
</style>
